<!-- #################################################################################### -->
<!-- ###### HERINCO                                                                ###### -->
<!-- ###### @author: Estefania Villada Chavarria                                   ###### -->
<!-- ###### @date: Octubre 2025                                                    ###### -->
<!-- #################################################################################### -->

<!-- #################################################################################### -->
<!-- ###### Sección de HTML                                                        ###### -->
<!-- #################################################################################### -->
<template>
  <div class="contenido">
    <!-- Descripción -->
    <div class="d-flex align-center mb-3">
      <v-icon color="blue darken-4" size="55">library_books</v-icon>
      <span class="ms-4 descripcion">
        Herramienta desarrollada para integrar y centralizar la información proveniente de los diferentes reportes de facturación utilizados previamente. <br>
        Permite: <br>
        <ul>
          <li>Obtener una visión unificada de los soportes, control de vencimiento de la resolución 2275 y estado RIPS.</li>
          <li>Facilitar el análisis y control de la facturación global en cuanto a soportes.</li>
          <li>Optimizar la trazabilidad y el seguimiento de los datos en un solo formato.</li>
        </ul>
      </span>
    </div>
    <v-divider color="#9E9E9" class="mb-3"></v-divider>
    
    <!-- Filtros para generar un reporte -->
    <section>
      <div class="mb-3">Filtros de búsqueda de expedientes:</div>
      <v-container class="container">
        <v-row>
          <!-- Campo para filtrar por fecha inicial -->
          <v-col cols="12" sm="3" md="3" class="pa-0">
            <v-menu ref="menuFechaInicio" v-model="menuFechaInicio" transition="scale-transition" min-width="290px"
              :close-on-content-click="false" :return-value.sync="fechaInicialCalendario" :nudge-top="25" offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-text-field ref="refFechaInicial" class="pr-1 pl-1"  v-model="fechaInicialCalendario" label="Fecha inicial *" v-bind="attrs" v-on="on"
                  outlined dense readonly :rules="rules.agregarRules" :disabled="inProgress === true">
                </v-text-field>
              </template>
              <v-date-picker v-model="fechaInicialCalendario" :first-day-of-week="1" :max="currentDate" locale="es-co" no-title scrollable @input="saveFechaInicial">
              </v-date-picker>
            </v-menu>
          </v-col>

          <!-- Campo para filtrar por fecha final -->
          <v-col cols="12" sm="3" md="3"  class="pa-0">
            <v-menu ref="menuFechaFinal" v-model="menuFechaFinal" transition="scale-transition" min-width="290px"
              :close-on-content-click="false" :return-value.sync="fechaFinalCalendario" :nudge-top="25" offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-text-field ref="refFechaFinal" class="pr-1 pl-1"  v-model="fechaFinalCalendario" label="Fecha final *" v-bind="attrs" v-on="on" 
                  outlined dense readonly :error-messages="rangoFechas" :rules="rules.agregarRules" :disabled="inProgress === true">
                </v-text-field>
              </template>
              <v-date-picker v-model="fechaFinalCalendario" :first-day-of-week="1" :max="currentDate" locale="es-co" no-title scrollable @input="saveFechaFinal">
              </v-date-picker>
            </v-menu>
          </v-col>

          <!-- Filtro por contrato -->
          <v-col cols="12" sm="3" md="3"  class="pa-0">
            <v-autocomplete ref="filtroContrato" class="pr-1 pl-1" v-model="filtro.contrato" label="Contrato" outlined dense :items="lista.contratos" 
              :menu-props="{offsetY: true, maxHeight: 200}"  clearable :disabled="inProgress === true" no-data-text="Sin resultados" 
              :item-text="item => `${item.idContrato} - ${item.nombreContrato}`" item-value="idContrato">
            </v-autocomplete>
          </v-col>

          <!-- Filtro por estado de Herincohan -->
          <v-col cols="12" sm="3" md="3" class="pa-0">
            <v-autocomplete ref="filtroEstado"  class="pr-1 pl-1" v-model="filtro.estado" label="Estado" outlined dense :items="lista.estados"
            :menu-props="{offsetY: true, maxHeight: 200}"  clearable :disabled="inProgress === true" no-data-text="Sin resultados">
            </v-autocomplete>
          </v-col>
        </v-row>
      </v-container>

      <!-- Botón de generar reporte -->
      <v-sheet class="d-flex align-center justify-center" :color="disableReport ? 'grey lighten-4' : 'green lighten-5'" height="65px" width="100%" rounded>
        <v-btn v-if="inProgress === false" color="success" @click="generarReporte()" :disabled="disableReport">
          <v-icon>download</v-icon>
          generar reporte
        </v-btn>
        <div v-else class="d-flex justify-center">
          <v-icon class="rotate-animation" color="#0d47a1" large>rotate_right</v-icon>
          <span class="d-flex align-center font-weight-black primary--text ms-2">CARGANDO</span>
        </div>
      </v-sheet>
    </section>

    <!-- Alerta cuando no se encuentran resultados -->
    <v-alert class="mt-5" icon="error" color="error" transition="scale-transition" :value="alert" outlined>
      <h3>No se encontraron resultados.</h3>
      <p class="no-resultados">Los datos ingresados no coinciden con ningún registro.
      <br>Verifica que los filtros sean correctos o intenta una búsqueda diferente.</p>
    </v-alert>

    <!--Snackbar de error-->
    <v-snackbar v-model="snackbar" top color="error" timeout="3000">
      {{ textoSnackbar }}
    </v-snackbar>

  </div>
</template>
 <!-- #################################################################################### -->
 <!-- ###### Sección de Scripts                                                     ###### -->
 <!-- #################################################################################### -->
<script>
import { mapState } from "vuex";
import { Role } from "../../../../router/role";

export default {
  name: "Facturacion",
  data() {
    return {
      filtro: {
        fechaInicial: '',
        fechaFinal: '',
        bodega: null,
        contrato: null,
        estado: null
      },
      lista: {
        bodegas: [],
        contratos: [],
        estados: [
          { text: "DEVUELTO" },
          { text: "AUDITADO" },
          { text: "RADICADO" },
          { text: "COMPLETO" },
          { text: "INCOMPLETO" },
          { text: "DOMICILIO" },
          { text: "NOTA CRÉDITO", value: 'NOTACREDITO' },
          { text: "FALTANTE" },
          { text: "PENDIENTE"},
          { text: "ANULADO" },
          { text: "ENTRADA DIRECTA", value: 'ENTRADADIRECTA' },
        ]
      },
      menuFechaInicio: false,
      menuFechaFinal: false,
      alert: false,
      rangoFechas: '',
      inProgress: false,
      fechaInicialCalendario: '',
      fechaFinalCalendario: '',
      rules: {
        agregarRules: [
          v => !!v || "Este campo es requerido.",
        ],
      },
      userRoles: {},
      roles: Role,
      bodegaUsuario: 0,
      snackbar: false,
      textoSnackbar: '',
      botonDisabled: false
    }
  },
  created() {
    this.userRoles = this.auth.roles;
  },
  mounted() {
    this.getContratos();
  },
  computed: {
    ...mapState(["auth", "enterprise"]),
    /**
     * Función computada que devuelve la fecha actual en formato ISO (YYYY-MM-DD)
     * para que en el v-date-picker no seleccione fechas posteriores a las del día actual. 
     */
    currentDate() {
      return new Date().toISOString().split('T')[0];
    },
    /** Función usada para deshabilitar o habilitar el botón para generar un reporte de acuerdo a las propiedades de los filtros. */
    disableReport() {
      return this.filtro.fechaInicial === '' || 
        this.filtro.fechaFinal === '' || 
        this.rangoFechas !== '' || 
        this.menuFechaInicio === true ||
        this.menuFechaFinal === true || this.botonDisabled;
    },
  },
  watch: {  
    /**
     * Este watcher se activa cuando alguna propiedad del objeto `filtro` cambia.
     * Es "deep", por lo que observa cambios en propiedades anidadas dentro de `filtro`.
     * Al detectar cambios, se encarga de desactivar la alerta (`alert` se establece en false).
     */
    filtro: {
      deep: true,
      handler() {
        this.alert = false;
      }
    },
  },
  methods: {
    /**
     * Este método verifica que la fecha inicial (`fechaUno`) sea anterior a la fecha final (`fechaDos`). Además, 
     * se asegura de que el rango de fechas seleccionadas no exceda un mes. Si alguna de estas condiciones no se cumple, 
     * establece un mensaje de error correspondiente y marca el estado como inválido (`invalid = true`).
     */
    validacionesFechas() {
      const fechaUno = new Date(`${this.filtro.fechaInicial}T00:00:00-05:00`);
      const fechaDos = new Date(`${this.filtro.fechaFinal}T00:00:00-05:00`);

      if (fechaUno > fechaDos) {
        this.rangoFechas = 'La fecha final debe ser mayor a la fecha inicial';
        return;
      }

      let fechaMaximaPermitida = new Date(fechaUno);
      fechaMaximaPermitida.setMonth(fechaMaximaPermitida.getMonth() + 1);

      if (fechaUno.getDate() === 1) {
        fechaMaximaPermitida.setDate(0);
      }

      if (fechaDos > fechaMaximaPermitida) {
        this.rangoFechas = 'Las fechas seleccionadas no deben exceder un mes';
      } else {
        this.rangoFechas = '';
      }
    },
    /** Guarda la fecha inicial seleccionada en el calendario y realiza las validaciones de rango de fechas. */
    saveFechaInicial() {
      this.$refs.menuFechaInicio.save(this.fechaInicialCalendario);
      this.filtro.fechaInicial = this.fechaInicialCalendario;
      this.validacionesFechas();
    },
    /** Limpia la fecha inicial seleccionada y restablece los campos relacionados. */
    cleanFechaInicial() {
      this.fechaInicialCalendario = '';
      this.$refs.menuFechaInicio.save('');
      this.filtro.fechaInicial = '';
    },
    /** Guarda la fecha final seleccionada en el calendario y realiza las validaciones de rango de fechas. */
    saveFechaFinal() {
      this.$refs.menuFechaFinal.save(this.fechaFinalCalendario);
      this.filtro.fechaFinal = this.fechaFinalCalendario;
      this.validacionesFechas();
    },
    /** Limpia la fecha final seleccionada y restablece los campos relacionados. */
    cleanFechaFinal() {
      this.fechaFinalCalendario = '';
      this.$refs.menuFechaFinal.save('');
      this.filtro.fechaFinal = '';
    },
    /** Método para generar el reporte */
    generarReporte() {
      const fechaInicio = `${this.filtro.fechaInicial} 00:00:00`;
      const fechaFinal = `${this.filtro.fechaFinal} 23:59:59`;
      this.inProgress = true;
      const params = {
        idEmpresa: this.enterprise.code,
        contratos: this.filtro.contrato,
        desde: fechaInicio,
        hasta: fechaFinal,
        estado: this.filtro.estado,
      };
      this.$http.get(`msa-reports/api/encabezado-expediente/consolidado-facturacion`, {
        params,
        responseType: 'blob'
      }).then(response => {
        if (response.status === 204 || response.data.size === 0) {
          this.inProgress = false;
          this.alert = true;
          return;
        }
        const filename = `reporte_facturacion_${this.filtro.fechaInicial.replace(/-/g, '_')}_al_${this.filtro.fechaFinal.replace(/-/g, '_')}.xlsx`;
        const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.click();
        
        URL.revokeObjectURL(url);
        
        this.clear();
        this.$refs.refFechaInicial?.reset();
        this.$refs.refFechaFinal?.reset();
        this.inProgress = false;
      }).catch(error => {
        console.log(error);
        this.handleError(error, 'Error al generar el reporte. Inténtelo de nuevo. Si persiste, contacte al administrador.');
        this.inProgress = false;
      });
    },
    formatDate(date) {
      const yyyy = date.getFullYear();
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const dd = String(date.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    },
    parseLocalDate(dateString) {
      const [year, month, day] = dateString.split('-').map(Number);
      return new Date(year, month - 1, day);
    },
    /** Método para listar las bodegas */
    getBodegas() {
      this.$http.get(`msa-reports/api/bodegas/expediente`, {
        params: {idEmpresa:this.enterprise.code}
      }).then(({ data }) => {
        this.lista.bodegas = [
          ...data,
        ];
      }).catch((error) => {
        this.handleError(error, 'Error al listar las bodegas. Inténtelo de nuevo. Si persiste, contacte al administrador.');
      });
    },
    /** Método para listar los contratos */
    getContratos() {
      this.$http.get(`msa-reports/api/contrato/expediente`, {
        params: {idEmpresa: this.enterprise.code}
      }).then(({ data }) => {
        this.lista.contratos = [
          ...data,
        ];
      }).catch((error) => {
        this.handleError(error, 'Error al listar los contratos. Inténtelo de nuevo. Si persiste, contacte al administrador.');
      });
    },
    /** Limpia y reinicia el estado del formulario y sus validaciones.*/
    clear() {
      this.filtro.contrato = null;
      this.filtro.bodega = null;
      this.cleanFechaInicial();
      this.cleanFechaFinal()
      this.rangoFechas = '';
      this.alert = false;
      this.inProgress = false;
      this.filtro.estado = null;
    },
    /**
    * Método para mostrar las alertas cuando genera error el back.
    * @param error - El error generado.
    * @param mensaje - El mensaje de error personalizado.
    */
    handleError(error, mensaje) {
      console.log(error);
      this.snackbar = true;
      this.textoSnackbar = `${mensaje}: ${error.response?.data?.message || error.message || error}`;
    },
  }
}
</script>
 <!-- #################################################################################### -->
 <!-- ###### Sección de Estilos                                                     ###### -->
 <!-- #################################################################################### -->
<style scoped>
.contenido {
  padding: 1rem;
  width: 100%;
  height: 86vh;
}
.descripcion {
  color: rgb(64, 62, 62); 
}
.no-resultados {
  font-size: 15px;
  margin: 0;
}
.rotate-animation {
  animation: rotate 2s linear infinite;
}
@keyframes rotate {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}
.container {
  max-width: none !important;
  width: 100% !important;
}
</style>